import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';

import {
  lookupRegistrationEvent,
  clearRegistrationEvent,
  selectRegistrationEvent,
  createBulkEventRegistration,
  fetchEventRegistrationsForEvent,
  deleteEventRegistration,
  resetCreateSuccess,
} from '../../actions/action_event_registration';

import { getStudents } from '../../actions/action_parent';

import {
  PageWrapper,
  Card,
  PageTitle,
  PageSubtitle,
  EventName,
  EventDetail,
  SectionTitle,
  StudentCard,
  StudentCardRow,
  StudentName,
  StudentMeta,
  Checkbox,
  RegisteredBadge,
  PrimaryButton,
  SecondaryButton,
  TextInput,
  SuccessMessage,
  ErrorMessage,
  LoginPrompt,
  Divider,
  FormRow,
  FormLabel,
  LoadingSpinner,
  BackLink,
  ConfirmationOverlay,
  ConfirmationDialog,
  ConfirmationTitle,
  ConfirmationText,
  ConfirmationActions,
  EventPickerCard,
  EventPickerName,
  EventPickerMeta,
  ClosedMessage,
  HelpSection,
  HelpTitle,
  HelpLink,
} from './EventRegistrationStyles';

function isSafeUrl(url) {
  if (!url) return false;
  return url.startsWith('http://') || url.startsWith('https://');
}

function EventRegistrationPage({ match, history }) {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const parent = useSelector((state) => state.parent);
  const eventRegistration = useSelector((state) => state.eventRegistration);

  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [studentFormData, setStudentFormData] = useState({});
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [addStudentError, setAddStudentError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [registeredCount, setRegisteredCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [submitError, setSubmitError] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showUnregisterConfirmation, setShowUnregisterConfirmation] =
    useState(false);
  const [unregisterRegistrationId, setUnregisterRegistrationId] =
    useState(null);
  const [unregisterStudentId, setUnregisterStudentId] = useState(null);
  const [unregistering, setUnregistering] = useState(false);

  // componentDidMount: lookup event, clear cookie
  useEffect(() => {
    const eventKey = match.params.key;
    setSelectedStudentIds([]);
    setStudentFormData({});
    setSubmitted(false);
    setRegisteredCount(0);
    setFailedCount(0);
    setSubmitError(null);
    setShowAddStudent(false);
    dispatch(lookupRegistrationEvent(eventKey));
    Cookies.remove('pics_event_key');

    // componentWillUnmount: clear event
    return () => {
      dispatch(clearRegistrationEvent());
    };
  }, [dispatch, match.params.key]);

  // Load students when user authenticates
  useEffect(() => {
    if (auth.currentUserId) {
      dispatch(getStudents(auth.currentUserId));
    }
  }, [dispatch, auth.currentUserId]);

  // Load registrations when event is selected/auto-selected
  useEffect(() => {
    if (eventRegistration.lookupEvent && auth.currentUserId) {
      dispatch(
        fetchEventRegistrationsForEvent(eventRegistration.lookupEvent.id),
      );
    }
  }, [dispatch, eventRegistration.lookupEvent, auth.currentUserId]);

  const isStudentRegistered = useCallback(
    (studentId) => {
      return eventRegistration.eventRegistrations.some(
        (r) => r.studentId === studentId,
      );
    },
    [eventRegistration.eventRegistrations],
  );

  const getRegistrationForStudent = useCallback(
    (studentId) => {
      return eventRegistration.eventRegistrations.find(
        (r) => r.studentId === studentId,
      );
    },
    [eventRegistration.eventRegistrations],
  );

  function handleToggleStudent(studentId) {
    if (isStudentRegistered(studentId)) return;
    setSelectedStudentIds((prev) => {
      if (prev.includes(studentId)) {
        return prev.filter((id) => id !== studentId);
      }
      return [...prev, studentId];
    });
    // Initialize form data entry if not present (preserve data on re-toggle)
    setStudentFormData((prev) => {
      if (prev[studentId]) return prev;
      return { ...prev, [studentId]: { grade: '', teacherLastName: '' } };
    });
  }

  function getStudentFormValue(studentId, field) {
    return studentFormData[studentId]?.[field] || '';
  }

  function setStudentFormValue(studentId, field, value) {
    setStudentFormData((prev) => ({
      ...prev,
      [studentId]: { ...prev[studentId], [field]: value },
    }));
  }

  const allStudentsHaveGrade =
    selectedStudentIds.length > 0 &&
    selectedStudentIds.every((id) => studentFormData[id]?.grade);

  function handleSelectEvent(event) {
    dispatch(selectRegistrationEvent(event));
    dispatch(resetCreateSuccess());
    setSelectedStudentIds([]);
    setStudentFormData({});
    setSubmitted(false);
    setFailedCount(0);
    setSubmitError(null);
  }

  function handleAddStudentToggle() {
    setShowAddStudent((prev) => !prev);
    setAddStudentError(null);
  }

  async function handleAddStudent() {
    if (!newFirstName.trim() || !newLastName.trim()) {
      setAddStudentError('First and last name are required.');
      return;
    }

    try {
      setSubmitting(true);
      setAddStudentError(null);
      const response = await axios.post('/api/v2/students', {
        first_name: newFirstName.trim(),
        last_name: newLastName.trim(),
      });

      const student = response.data;
      setShowAddStudent(false);
      setNewFirstName('');
      setNewLastName('');
      setSelectedStudentIds((prev) => [...prev, student.id]);
      setStudentFormData((prev) => ({
        ...prev,
        [student.id]: { grade: '', teacherLastName: '' },
      }));
      setSubmitting(false);

      // Refresh students list via Redux instead of page reload
      if (auth.currentUserId) {
        dispatch(getStudents(auth.currentUserId));
      }
    } catch (err) {
      const errorMsg =
        err.response?.data?.errors?.student ||
        err.response?.data?.message ||
        'Failed to add student.';
      setAddStudentError(errorMsg);
      setSubmitting(false);
    }
  }

  async function handleSubmitRegistration() {
    const event = eventRegistration.lookupEvent;
    if (selectedStudentIds.length === 0 || !event) return;

    const count = selectedStudentIds.length;
    setSubmitting(true);
    setSubmitError(null);
    setShowConfirmation(false);

    try {
      const students = selectedStudentIds.map((id) => ({
        student_id: id,
        teacher_last_name: getStudentFormValue(id, 'teacherLastName'),
        grade: getStudentFormValue(id, 'grade'),
      }));

      const result = await dispatch(
        createBulkEventRegistration({
          event_id: event.id,
          students: students,
        }),
      );

      if (result.error) {
        setSubmitError(
          result.payload?.response?.data?.error ||
            'Registration failed. Please try again.',
        );
        return;
      }

      const successCount = result.payload?.data?.registrations?.length || 0;
      const errorCount = result.payload?.data?.errors?.length || 0;

      setRegisteredCount(successCount);
      setFailedCount(errorCount);
      setSubmitted(true);
      setSelectedStudentIds([]);
      setStudentFormData({});

      // Refresh registrations to update badges
      dispatch(fetchEventRegistrationsForEvent(event.id));
    } catch (err) {
      setSubmitError(
        err.response?.data?.error || 'Registration failed. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUnregister() {
    if (!unregisterRegistrationId) return;

    setUnregistering(true);
    try {
      await dispatch(deleteEventRegistration(unregisterRegistrationId));
      const event = eventRegistration.lookupEvent;
      if (event) {
        dispatch(fetchEventRegistrationsForEvent(event.id));
      }
    } catch (err) {
      // Silently handle — the student card will remain registered if it fails
    }
    setUnregistering(false);
    setShowUnregisterConfirmation(false);
    setUnregisterRegistrationId(null);
    setUnregisterStudentId(null);
  }

  function renderEventPicker() {
    const { lookupEvents, selectedEvent } = eventRegistration;

    if (!lookupEvents || lookupEvents.length <= 1) return null;

    return (
      <Card>
        <SectionTitle>Which event are you registering for?</SectionTitle>
        {lookupEvents.map((event) => {
          const dateDisplay =
            event.dateRangeDisplay ||
            (event.shootDate
              ? new Date(event.shootDate + 'T00:00').toLocaleDateString(
                  'en-US',
                  {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  },
                )
              : null);

          return (
            <EventPickerCard
              key={event.id}
              selected={selectedEvent && selectedEvent.id === event.id}
              closed={event.registrationClosed}
              onClick={() => handleSelectEvent(event)}
            >
              <EventPickerName>
                {event.name}
                {event.isRetake && (
                  <RegisteredBadge style={{ background: '#f57c00' }}>
                    Retake
                  </RegisteredBadge>
                )}
                {event.registrationClosed && (
                  <RegisteredBadge style={{ background: '#9aa0a6' }}>
                    Closed
                  </RegisteredBadge>
                )}
              </EventPickerName>
              {event.venueName && (
                <EventPickerMeta>{event.venueName}</EventPickerMeta>
              )}
              {dateDisplay && <EventPickerMeta>{dateDisplay}</EventPickerMeta>}
            </EventPickerCard>
          );
        })}
      </Card>
    );
  }

  function renderEventInfo() {
    const event = eventRegistration.lookupEvent;
    if (!event) return null;

    const { lookupEvents } = eventRegistration;
    if (lookupEvents && lookupEvents.length > 1) return null;

    const dateDisplay =
      event.dateRangeDisplay ||
      (event.shootDate
        ? new Date(event.shootDate + 'T00:00').toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        : null);

    return (
      <Card>
        <EventName>{event.name}</EventName>
        {event.venueName && <EventDetail>{event.venueName}</EventDetail>}
        {dateDisplay && <EventDetail>{dateDisplay}</EventDetail>}
        {event.shortDescription && (
          <>
            <Divider />
            <EventDetail>{event.shortDescription}</EventDetail>
          </>
        )}
      </Card>
    );
  }

  function renderLoginPrompt() {
    const eventKey = match.params.key;

    return (
      <Card>
        <LoginPrompt>
          <SectionTitle>Sign in to register</SectionTitle>
          <p style={{ color: '#5f6368', marginBottom: '20px' }}>
            You need to be signed in to register your student for this event.
          </p>
          <Link
            to={{
              pathname: '/account-lookup',
              state: {
                redirectTo: `/events/${eventKey}/register`,
              },
            }}
            style={{ textDecoration: 'none' }}
          >
            <PrimaryButton as="div">Sign In</PrimaryButton>
          </Link>
          <p style={{ marginTop: '16px', color: '#5f6368' }}>
            Don&apos;t have an account?{' '}
            <Link
              to={{
                pathname: '/new-account',
                state: {
                  redirectTo: `/events/${eventKey}/register`,
                },
              }}
              style={{ color: '#1a73e8' }}
            >
              Sign up
            </Link>
          </p>
        </LoginPrompt>
      </Card>
    );
  }

  function renderAddStudentForm() {
    return (
      <div>
        <FormLabel>First Name</FormLabel>
        <TextInput
          type="text"
          placeholder="First name"
          value={newFirstName}
          onChange={(e) => setNewFirstName(e.target.value)}
        />
        <FormLabel>Last Name</FormLabel>
        <TextInput
          type="text"
          placeholder="Last name"
          value={newLastName}
          onChange={(e) => setNewLastName(e.target.value)}
        />
        {addStudentError && <ErrorMessage>{addStudentError}</ErrorMessage>}
        <FormRow>
          <PrimaryButton onClick={handleAddStudent} disabled={submitting}>
            {submitting ? 'Adding...' : 'Add Student'}
          </PrimaryButton>
          {showAddStudent && (
            <SecondaryButton onClick={handleAddStudentToggle}>
              Cancel
            </SecondaryButton>
          )}
        </FormRow>
      </div>
    );
  }

  function renderStudentSelection() {
    const { students } = parent;

    if (!students || students.length === 0) {
      return (
        <Card>
          <SectionTitle>Add a Student</SectionTitle>
          <p style={{ color: '#5f6368', marginBottom: '16px' }}>
            You haven&apos;t added any students yet. Add a student to register
            them for this event.
          </p>
          {renderAddStudentForm()}
        </Card>
      );
    }

    return (
      <Card>
        <SectionTitle>Select students to register</SectionTitle>
        {students.map((student) => {
          const registered = isStudentRegistered(student.id);
          const isSelected = selectedStudentIds.includes(student.id);

          return (
            <StudentCard
              key={student.id}
              selected={isSelected}
              onClick={() => handleToggleStudent(student.id)}
            >
              <StudentCardRow>
                <Checkbox checked={isSelected || registered} />
                <div>
                  <StudentName>
                    {student.firstName} {student.lastName}
                    {registered && (
                      <RegisteredBadge>Registered</RegisteredBadge>
                    )}
                  </StudentName>
                  {registered && (
                    <StudentMeta>
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          const reg = getRegistrationForStudent(student.id);
                          if (reg) {
                            setShowUnregisterConfirmation(true);
                            setUnregisterRegistrationId(reg.id);
                            setUnregisterStudentId(student.id);
                          }
                        }}
                        style={{
                          color: '#5f6368',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                      >
                        Unregister
                      </span>
                    </StudentMeta>
                  )}
                </div>
              </StudentCardRow>
            </StudentCard>
          );
        })}

        <Divider />

        {showAddStudent ? (
          renderAddStudentForm()
        ) : (
          <SecondaryButton onClick={handleAddStudentToggle}>
            + Add a new student
          </SecondaryButton>
        )}
      </Card>
    );
  }

  function gradeOptions() {
    const event = eventRegistration.lookupEvent;
    return event?.gradeOptions || [];
  }

  function renderRegistrationForm() {
    const { createError } = eventRegistration;
    const error = submitError || createError;

    if (selectedStudentIds.length === 0) return null;

    const { students } = parent;
    const selectedStudents = students.filter((s) =>
      selectedStudentIds.includes(s.id),
    );

    if (selectedStudents.length === 0) return null;

    const studentCount = selectedStudents.length;

    return (
      <Card>
        <SectionTitle>
          Registering {studentCount} Student{studentCount > 1 ? 's' : ''}
        </SectionTitle>
        {selectedStudents.map((student, index) => (
          <div
            key={student.id}
            style={{
              paddingBottom: '16px',
              marginBottom: '16px',
              borderBottom:
                index < selectedStudents.length - 1
                  ? '1px solid #f0f0f0'
                  : 'none',
            }}
          >
            <div
              style={{
                fontSize: '15px',
                fontWeight: 500,
                marginBottom: '12px',
              }}
            >
              {student.firstName} {student.lastName}
            </div>
            <FormLabel>Grade (required)</FormLabel>
            <select
              value={getStudentFormValue(student.id, 'grade')}
              onChange={(e) =>
                setStudentFormValue(student.id, 'grade', e.target.value)
              }
              style={{
                width: '100%',
                padding: '12px 14px',
                borderRadius: '8px',
                border: '1px solid #d9d9d9',
                marginBottom: '12px',
              }}
            >
              {gradeOptions().map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={option.value === ''}
                >
                  {option.label}
                </option>
              ))}
            </select>
            <FormLabel>Teacher Last Name (optional)</FormLabel>
            <TextInput
              type="text"
              placeholder="Teacher last name"
              value={getStudentFormValue(student.id, 'teacherLastName')}
              onChange={(e) =>
                setStudentFormValue(
                  student.id,
                  'teacherLastName',
                  e.target.value,
                )
              }
              maxLength={255}
            />
          </div>
        ))}
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <PrimaryButton
          onClick={() => setShowConfirmation(true)}
          disabled={submitting || !allStudentsHaveGrade}
        >
          {submitting
            ? 'Registering...'
            : `Register ${studentCount} Student${studentCount > 1 ? 's' : ''}`}
        </PrimaryButton>
      </Card>
    );
  }

  function renderConfirmationDialog() {
    if (!showConfirmation) return null;

    const { students } = parent;
    const selectedStudents = students.filter((s) =>
      selectedStudentIds.includes(s.id),
    );
    const event = eventRegistration.lookupEvent;
    const options = gradeOptions();

    function gradeLabel(value) {
      const option = options.find((o) => o.value === value);
      return option ? option.label : value;
    }

    return (
      <ConfirmationOverlay onClick={() => setShowConfirmation(false)}>
        <ConfirmationDialog onClick={(e) => e.stopPropagation()}>
          <ConfirmationTitle>Confirm Registration</ConfirmationTitle>
          <ConfirmationText>
            Register for <strong>{event.name}</strong>:
          </ConfirmationText>
          <div style={{ marginBottom: '20px' }}>
            {selectedStudents.map((s) => {
              const studentGrade = getStudentFormValue(s.id, 'grade');
              const teacher = getStudentFormValue(s.id, 'teacherLastName');
              return (
                <div
                  key={s.id}
                  style={{
                    padding: '6px 0',
                    borderBottom: '1px solid #f0f0f0',
                    fontSize: '14px',
                    color: '#3c4043',
                  }}
                >
                  <strong>
                    {s.firstName} {s.lastName}
                  </strong>
                  {' — '}
                  {gradeLabel(studentGrade)}
                  {teacher && `, Teacher: ${teacher}`}
                </div>
              );
            })}
          </div>
          <ConfirmationActions>
            <SecondaryButton
              onClick={() => setShowConfirmation(false)}
              style={{ width: 'auto' }}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={handleSubmitRegistration}
              disabled={submitting || !allStudentsHaveGrade}
              style={{ width: 'auto' }}
            >
              {submitting ? 'Registering...' : 'Yes, Register'}
            </PrimaryButton>
          </ConfirmationActions>
        </ConfirmationDialog>
      </ConfirmationOverlay>
    );
  }

  function renderUnregisterConfirmationDialog() {
    if (!showUnregisterConfirmation) return null;

    const { students } = parent;
    const student = students.find((s) => s.id === unregisterStudentId);
    const event = eventRegistration.lookupEvent;

    if (!student || !event) return null;

    const studentName = `${student.firstName} ${student.lastName}`;

    return (
      <ConfirmationOverlay
        onClick={() => {
          setShowUnregisterConfirmation(false);
          setUnregisterRegistrationId(null);
          setUnregisterStudentId(null);
        }}
      >
        <ConfirmationDialog onClick={(e) => e.stopPropagation()}>
          <ConfirmationTitle>Confirm Unregistration</ConfirmationTitle>
          <ConfirmationText>
            Are you sure you want to unregister <strong>{studentName}</strong>{' '}
            from <strong>{event.name}</strong>?
          </ConfirmationText>
          <ConfirmationActions>
            <SecondaryButton
              onClick={() => {
                setShowUnregisterConfirmation(false);
                setUnregisterRegistrationId(null);
                setUnregisterStudentId(null);
              }}
              style={{ width: 'auto' }}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={handleUnregister}
              disabled={unregistering}
              style={{ width: 'auto' }}
            >
              {unregistering ? 'Removing...' : 'Yes, Unregister'}
            </PrimaryButton>
          </ConfirmationActions>
        </ConfirmationDialog>
      </ConfirmationOverlay>
    );
  }

  function renderSuccess() {
    const { createSuccess } = eventRegistration;

    if (!submitted && !createSuccess) return null;

    return (
      <div>
        <SuccessMessage>
          <div>
            <strong>
              {failedCount > 0 && registeredCount > 0
                ? `${registeredCount} of ${
                    registeredCount + failedCount
                  } students registered`
                : 'Registration successful!'}
            </strong>
            {failedCount > 0 && registeredCount > 0 && (
              <p
                style={{
                  margin: '4px 0 0',
                  fontSize: '14px',
                  color: '#b45309',
                }}
              >
                {failedCount} student{failedCount !== 1 ? 's were' : ' was'}{' '}
                already registered and {failedCount !== 1 ? 'were' : 'was'}{' '}
                skipped.
              </p>
            )}
            <p style={{ margin: '4px 0 0', fontSize: '14px' }}>
              {registeredCount > 0 ? (
                <>
                  Your student{registeredCount !== 1 ? 's have' : ' has'} been
                  registered for this event. You can view your registrations on
                  your{' '}
                  <Link to="/my-account" style={{ color: '#137333' }}>
                    account page
                  </Link>
                  .
                </>
              ) : (
                'All selected students were already registered for this event.'
              )}
            </p>
          </div>
        </SuccessMessage>
        <p style={{ margin: '16px 0 16px', fontSize: '14px' }}>
          <Link to="/dashboard" style={{ color: '#1a73e8' }}>
            Return to Dashboard
          </Link>
        </p>
      </div>
    );
  }

  function renderRegistrationClosed() {
    const event = eventRegistration.lookupEvent;
    if (!event || !event.registrationClosed) return null;

    return (
      <ClosedMessage>
        <h3>Registration Closed</h3>
        <p>Registration for this event has closed.</p>
        {event.allowOrderingAfterClose && (
          <Link
            to={`/dashboard?key=${event.key}`}
            style={{
              display: 'inline-block',
              marginTop: '16px',
              padding: '10px 24px',
              backgroundColor: '#1a73e8',
              color: '#fff',
              fontWeight: '500',
              textDecoration: 'none',
              borderRadius: '4px',
            }}
          >
            Order Photos
          </Link>
        )}
        <Link
          to="/dashboard"
          style={{
            display: 'inline-block',
            marginTop: event.allowOrderingAfterClose ? '12px' : '16px',
            color: '#1a73e8',
            fontWeight: '500',
            textDecoration: 'none',
          }}
        >
          Return to Dashboard
        </Link>
      </ClosedMessage>
    );
  }

  function renderHelpSection() {
    const event = eventRegistration.lookupEvent;
    if (!event) return null;

    return (
      <HelpSection>
        <HelpTitle>Need help?</HelpTitle>
        {isSafeUrl(event.studioUrl) && (
          <div style={{ marginBottom: '6px' }}>
            <HelpLink
              href={event.studioUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Visit {event.companyName || 'our studio website'} →
            </HelpLink>
          </div>
        )}
        {isSafeUrl(event.customerSupportUrl) && (
          <div style={{ marginBottom: '6px' }}>
            <HelpLink
              href={event.customerSupportUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Contact support →
            </HelpLink>
          </div>
        )}
        {!isSafeUrl(event.studioUrl) &&
          !isSafeUrl(event.customerSupportUrl) && (
            <p>
              If you need assistance, reach out to your photographer or school
              office.
            </p>
          )}
      </HelpSection>
    );
  }

  const { lookupEvents, lookupEvent, lookupError } = eventRegistration;
  const isLoggedIn = auth.currentUserId && auth.signupCompleted;

  if (lookupError) {
    return (
      <PageWrapper>
        <Card>
          <PageTitle>Event Not Found</PageTitle>
          <p
            style={{
              textAlign: 'center',
              color: '#5f6368',
              marginBottom: '16px',
            }}
          >
            {lookupError}
          </p>
          <BackLink to="/dashboard">Go to Dashboard</BackLink>
        </Card>
      </PageWrapper>
    );
  }

  if (!lookupEvents || lookupEvents.length === 0) {
    return (
      <PageWrapper>
        <LoadingSpinner>Loading event details...</LoadingSpinner>
      </PageWrapper>
    );
  }

  const needsEventSelection = lookupEvents.length > 1 && !lookupEvent;
  const isClosed = lookupEvent && lookupEvent.registrationClosed;

  return (
    <PageWrapper style={{ marginTop: '100px' }}>
      <PageTitle>Event Registration</PageTitle>
      <PageSubtitle>Register your student for picture day</PageSubtitle>

      {renderEventInfo()}
      {renderEventPicker()}

      {isClosed && renderRegistrationClosed()}

      {!isClosed && renderSuccess()}

      {!isClosed && !needsEventSelection && isLoggedIn && (
        <>
          {renderStudentSelection()}
          {renderRegistrationForm()}
        </>
      )}

      {!isClosed && !needsEventSelection && !isLoggedIn && renderLoginPrompt()}

      {renderHelpSection()}

      {renderConfirmationDialog()}
      {renderUnregisterConfirmationDialog()}
    </PageWrapper>
  );
}

export default EventRegistrationPage;
